import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../auth/adminPermissions";
import RequirePermission from "../../auth/RequirePermission";
import AdminPageHeader from "../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../components/midpoint/admin/layout/AdminStatePanel";
import WeatherSchedulerSummaryCards from "../../components/midpoint/admin/weather/scheduler/WeatherSchedulerSummaryCards";
import WeatherSchedulerJobTable from "../../components/midpoint/admin/weather/scheduler/WeatherSchedulerJobTable";
import {
  fetchWeatherSchedulerJobs,
  patchWeatherSchedulerJob,
  runWeatherSchedulerJob,
} from "../../weather/colombiaWeatherSchedulerApi";

function WeatherSchedulerPageContent() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.WEATHER_LAYERS_MANAGE);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState(null);
  const [runningJobKey, setRunningJobKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const payload = await fetchWeatherSchedulerJobs();
      setStatus(payload);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load scheduler status.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 4000);
  };

  const handleRunNow = async (job) => {
    if (!canManage) {
      return;
    }
    setRunningJobKey(job.jobKey);
    try {
      const result = await runWeatherSchedulerJob(job.jobKey, { dryRun: false });
      showToast(
        `${job.jobName}: ${result.status || result.reason || "completed"}`,
        result.status === "failed" ? "error" : "success"
      );
      await load();
    } catch (error) {
      showToast(error.message || "Run failed", "error");
    } finally {
      setRunningJobKey(null);
    }
  };

  const handleToggleEnabled = async (job) => {
    if (!canManage) {
      return;
    }
    try {
      await patchWeatherSchedulerJob(job.jobKey, { enabled: !job.enabled });
      showToast(`${job.jobName} ${job.enabled ? "disabled" : "enabled"}`, "success");
      await load();
    } catch (error) {
      showToast(error.message || "Update failed", "error");
    }
  };

  const handleEditInterval = async (job) => {
    if (!canManage) {
      return;
    }
    const input = window.prompt(
      `Interval in minutes for ${job.jobName}`,
      String(job.intervalMinutes || 5)
    );
    if (input == null) {
      return;
    }
    const intervalMinutes = Number(input);
    if (!Number.isFinite(intervalMinutes) || intervalMinutes < 1) {
      showToast("Interval must be a positive number.", "error");
      return;
    }
    try {
      await patchWeatherSchedulerJob(job.jobKey, { intervalMinutes });
      showToast(`${job.jobName} interval updated`, "success");
      await load();
    } catch (error) {
      showToast(error.message || "Update failed", "error");
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title="Weather job scheduler"
        subtitle="Colombia weather automation — health checks, monitoring, and optional ingestion jobs."
        actions={
          <Link className="admin-btn admin-btn--secondary" to="/admin/weather/sources">
            Weather sources
          </Link>
        }
      />

      {toast ? (
        <div
          className={`admin-toast admin-toast--${toast.type === "error" ? "error" : "success"}`}
          role="status"
        >
          {toast.message}
        </div>
      ) : null}

      {loading ? <AdminStatePanel variant="loading" message="Loading scheduler…" /> : null}
      {!loading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={load} />
      ) : null}

      {!loading && !errorMessage && status ? (
        <>
          <WeatherSchedulerSummaryCards status={status} />
          <WeatherSchedulerJobTable
            jobs={status.jobs || []}
            canManage={canManage}
            runningJobKey={runningJobKey}
            onRunNow={handleRunNow}
            onToggleEnabled={handleToggleEnabled}
            onEditInterval={handleEditInterval}
          />
          {status.lastSchedulerError ? (
            <p className="admin-muted">Last scheduler error: {status.lastSchedulerError}</p>
          ) : null}
        </>
      ) : null}
    </AdminPageLayout>
  );
}

export default function WeatherSchedulerPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.WEATHER_LAYERS_VIEW}>
      <WeatherSchedulerPageContent />
    </RequirePermission>
  );
}
